import { DOCUMENT } from '@angular/common'
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core'
import { ChunkLoadErrorService } from './chunk-load-error.service'

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly document = inject(DOCUMENT)
  private readonly zone = inject(NgZone)
  private readonly prompt = inject(ChunkLoadErrorService)
  private timer?: ReturnType<typeof setInterval>
  private request?: AbortController
  private destroyed = false
  private onVisible?: () => void

  constructor() {
    const current = this.mainBundle(this.document)
    const view = this.document.defaultView
    // Development servers do not emit a content-hashed entry point.
    if (!current || !view?.fetch) return
    // The app root, not `index.html`: `serve` 301s `/index.html` to a clean URL.
    const indexUrl = new URL('./', this.document.baseURI)
    // Hidden tabs skip polls, so check as soon as one is looked at again —
    // before the merchant navigates into a page the deployment changed.
    const onVisible = () => {
      if (this.document.visibilityState === 'visible') void this.check(current, indexUrl)
    }
    this.onVisible = onVisible
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => void this.check(current, indexUrl), 60_000)
      this.document.addEventListener('visibilitychange', onVisible)
      void this.check(current, indexUrl)
    })
  }

  ngOnDestroy(): void {
    this.destroyed = true
    clearInterval(this.timer)
    if (this.onVisible) this.document.removeEventListener('visibilitychange', this.onVisible)
    this.request?.abort()
  }

  private mainBundle(document: Document): string | undefined {
    return Array.from(document.querySelectorAll('script[src]'))
      .map(script => script.getAttribute('src') ?? '')
      .find(src => /(?:^|\/)main[-.][\w-]+\.js$/.test(src))
  }

  private async check(current: string, indexUrl: URL): Promise<void> {
    const view = this.document.defaultView
    if (!view) return
    if (this.destroyed || this.request || this.prompt.visible() || this.document.visibilityState === 'hidden') return
    const request = new AbortController()
    this.request = request
    const timeout = setTimeout(() => request.abort(), 10_000)
    try {
      const response = await view.fetch(indexUrl.href, { cache: 'no-store', signal: request.signal })
      // Redirects are followed, not rejected: the checks below already refuse
      // anything that isn't the dashboard's own index page.
      if (!response.ok) return
      const html = new DOMParser().parseFromString(await response.text(), 'text/html')
      const latest = this.mainBundle(html)
      if (!this.destroyed && html.querySelector('app-root') && latest && latest !== current) {
        this.zone.run(() => this.prompt.showUpdatePrompt())
      }
    } catch {
      // Offline tabs and temporary deployment failures retry on the next poll.
    } finally {
      clearTimeout(timeout)
      this.request = undefined
    }
  }
}
